import React, { useEffect, useMemo, useState } from 'react';
import NavigationBar from '@/components/ui/NavigationBar';
import Icon from '@/components/AppIcon';
import Button from '@/components/ui/Button';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/hooks/useSubscription';
import { hasFeatureAccess } from '@/config/featureAccess';
import { ugcBrandsService } from '@/services/ugcBrandsService';
import { NovaChatProvider } from '@/contexts/NovaChatContext';
import NovaChat from '@/components/NovaChat';
import UGCLayout from '@/components/layouts/UGCLayout';

const UGCBrandsPage = () => {
  const { profile } = useAuth();
  const { ugcTier } = useSubscription();
  const userTier = ugcTier || profile?.ugc_tier || 'free';
  const canAccess = hasFeatureAccess('app/ugc-dashboard', userTier);

  const [brands, setBrands] = useState([]);
  const [brandForm, setBrandForm] = useState({ name: '', category: 'CPG', website: '', email: '', notes: '', tags: '', status: 'prospect' });
  const [brandFilter, setBrandFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [brandSearch, setBrandSearch] = useState('');
  const [editingBrandId, setEditingBrandId] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [showForm, setShowForm] = useState(false);

  const categories = ['all', 'Beauty', 'CPG', 'Fitness', 'Apparel', 'Home', 'Tech', 'Apps', 'Food & Bev', 'Wellness'];
  const statuses = ['all', 'prospect', 'contacted', 'negotiating', 'partnership', 'rejected'];

  const ugcNavItems = [
    { path: '/app/ugc-hub', label: 'Creator Hub', icon: 'Compass' },
    { path: '/app/ugc-brands', label: 'Brands', icon: 'Database' },
    { path: '/app/ugc-crm', label: 'CRM', icon: 'Users' },
    { path: '/app/ugc-community', label: 'Community', icon: 'MessageCircle' },
    { path: '/app/ugc-scripts', label: 'Scripts', icon: 'FileText' },
    { path: '/app/teleprompter', label: 'Teleprompter', icon: 'PlayCircle' },
  ];

  useEffect(() => {
    const loadBrands = async () => {
      try {
        const list = await ugcBrandsService.list();
        setBrands(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error('Failed to load brands', err);
      }
    };
    loadBrands();
  }, []);

  const allBrands = useMemo(() => {
    return brands
      .filter((brand) => {
        const matchesCategory = brandFilter === 'all' || brand.category === brandFilter;
        const matchesStatus = statusFilter === 'all' || (brand.status || 'prospect') === statusFilter;
        const term = brandSearch.trim().toLowerCase();
        const matchesSearch = !term
          || brand.name.toLowerCase().includes(term)
          || (brand.notes || '').toLowerCase().includes(term)
          || (brand.tags || []).some((t) => t.toLowerCase().includes(term));
        return matchesCategory && matchesSearch && matchesStatus;
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [brandFilter, statusFilter, brandSearch, brands]);

  const handleAddBrand = async (e) => {
    e.preventDefault();
    if (!brandForm.name.trim()) return;
    const tags = brandForm.tags
      ? brandForm.tags.split(',').map((t) => t.trim()).filter(Boolean)
      : [];
    const payload = {
      name: brandForm.name.trim(),
      category: brandForm.category,
      website: brandForm.website?.trim(),
      email: brandForm.email?.trim(),
      notes: brandForm.notes?.trim(),
      status: brandForm.status || 'prospect',
      tags
    };
    const action = editingBrandId
      ? ugcBrandsService.update(editingBrandId, payload)
      : ugcBrandsService.create(profile?.id, payload);
    try {
      const result = await action;
      if (editingBrandId) {
        setBrands(brands.map((b) => (b.id === editingBrandId ? result : b)));
      } else {
        setBrands([result, ...brands]);
      }
      setBrandForm({ name: '', category: 'CPG', website: '', email: '', notes: '', tags: '', status: 'prospect' });
      setEditingBrandId(null);
      setShowForm(false);
    } catch (err) {
      console.error('Failed to save brand', err);
    }
  };

  const handleCopyEmail = (brand) => {
    const template = `Subject: Partnership Opportunity with [Your Name]

Hi ${brand.name} Team,

I'm a UGC creator specializing in ${brand.category.toLowerCase()} content with [X] engaged followers. I love your brand and would be excited to create authentic content showcasing your products.

My recent campaigns have achieved:
• Average engagement rate: [X]%
• Reach: [X] views per video
• Platform: TikTok/Instagram/YouTube

I'd love to discuss a partnership. Are you currently working with creators?

Best regards,
[Your Name]
${brand.email || '[brand email]'}`;

    navigator.clipboard.writeText(template);
    alert('Outreach email template copied to clipboard!');
  };

  const getStatusColor = (status) => {
    const colors = {
      prospect: 'bg-gray-100 text-gray-700 border-gray-200',
      contacted: 'bg-blue-100 text-blue-700 border-blue-200',
      negotiating: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      partnership: 'bg-green-100 text-green-700 border-green-200',
      rejected: 'bg-red-100 text-red-700 border-red-200'
    };
    return colors[status] || colors.prospect;
  };

  const getCategoryIcon = (category) => {
    const icons = {
      Beauty: 'Sparkles',
      CPG: 'Package',
      Fitness: 'Activity',
      Apparel: 'ShoppingBag',
      Home: 'Home',
      Tech: 'Cpu',
      Apps: 'Smartphone',
      'Food & Bev': 'Coffee',
      Wellness: 'Heart'
    };
    return icons[category] || 'Tag';
  };

  if (!canAccess) {
    return (
      <UGCLayout className="pb-20">
        <NavigationBar items={ugcNavItems} hideMore homePath="/app/ugc-dashboard" />
        <main className="max-w-5xl mx-auto px-4 py-12">
          <div className="rounded-2xl border border-white/10 bg-slate-800/40 p-8 shadow-lg">
            <Icon name="Lock" size={32} className="text-amber-300 mb-3" />
            <h3 className="text-xl font-semibold text-white mb-2">UGC Brand Access Required</h3>
            <p className="text-sm text-amber-100/80 mb-4">Unlock the brand database and outreach tools.</p>
            <Button onClick={() => (window.location.href = '/ugc-subscription')} className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-lg">
              View UGC Plans
            </Button>
          </div>
        </main>
      </UGCLayout>
    );
  }

  const statsByCategory = categories.filter(c => c !== 'all').reduce((acc, cat) => {
    acc[cat] = brands.filter(b => b.category === cat).length;
    return acc;
  }, {});

  const statsByStatus = statuses.filter(s => s !== 'all').reduce((acc, status) => {
    acc[status] = brands.filter(b => (b.status || 'prospect') === status).length;
    return acc;
  }, {});

  return (
    <UGCLayout className="pb-20">
      <NavigationBar items={ugcNavItems} hideMore hideLocks homePath="/app/ugc-dashboard" />
      <main className="max-w-7xl mx-auto px-4 py-10">
        {/* Header */}
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/20 border border-blue-500/40 mb-4">
            <Icon name="Database" size={16} color="#93C5FD" />
            <span className="text-xs font-semibold text-blue-100">Brand Database</span>
          </div>
          <h1 className="text-4xl font-bold text-white mb-3">Discover & Track Brands</h1>
          <p className="text-amber-100/80 max-w-3xl text-lg">
            Build your brand pipeline, organize outreach, and manage partnerships. Search, filter, and track everything in one place.
          </p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="rounded-xl border border-white/10 bg-white/5 p-4 shadow-sm">
            <Icon name="Database" size={20} className="text-purple-300 mb-2" />
            <p className="text-2xl font-bold text-white">{allBrands.length}</p>
            <p className="text-xs text-amber-100/80">Total Brands</p>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/5 p-4 shadow-sm">
            <Icon name="Mail" size={20} className="text-blue-300 mb-2" />
            <p className="text-2xl font-bold text-white">{statsByStatus.contacted || 0}</p>
            <p className="text-xs text-amber-100/80">Contacted</p>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/5 p-4 shadow-sm">
            <Icon name="MessageCircle" size={20} className="text-yellow-300 mb-2" />
            <p className="text-2xl font-bold text-white">{statsByStatus.negotiating || 0}</p>
            <p className="text-xs text-amber-100/80">Negotiating</p>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/5 p-4 shadow-sm">
            <Icon name="Check" size={20} className="text-green-300 mb-2" />
            <p className="text-2xl font-bold text-white">{statsByStatus.partnership || 0}</p>
            <p className="text-xs text-amber-100/80">Partnerships</p>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/5 p-4 shadow-sm">
            <Icon name="Users" size={20} className="text-gray-300 mb-2" />
            <p className="text-2xl font-bold text-white">{brands.filter((b) => b.created_by === profile?.id).length}</p>
            <p className="text-xs text-amber-100/80">Your List</p>
          </div>
        </div>

        {/* Filters and Controls */}
        <div className="rounded-xl border border-white/10 bg-white/5 p-4 shadow-sm mb-6">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="flex gap-2 items-center flex-wrap">
              <select
                className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white"
                value={brandFilter}
                onChange={(e) => setBrandFilter(e.target.value)}
              >
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat === 'all' ? 'All Categories' : cat}
                  </option>
                ))}
              </select>
              <select
                className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                {statuses.map((status) => (
                  <option key={status} value={status}>
                    {status === 'all' ? 'All Statuses' : status.charAt(0).toUpperCase() + status.slice(1)}
                  </option>
                ))}
              </select>
              <div className="relative">
                <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-amber-100/70" />
                <input
                  className="rounded-lg border border-white/10 bg-white/5 pl-9 pr-3 py-2 text-sm w-64 text-white placeholder:text-white/50"
                  placeholder="Search brands, tags, notes..."
                  value={brandSearch}
                  onChange={(e) => setBrandSearch(e.target.value)}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <div className="flex rounded-lg border border-white/10 bg-white/5">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`px-3 py-2 rounded-l-lg ${viewMode === 'grid' ? 'bg-amber-500 text-slate-950' : 'text-amber-100/70'}`}
                >
                  <Icon name="Grid" size={16} />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`px-3 py-2 rounded-r-lg ${viewMode === 'list' ? 'bg-amber-500 text-slate-950' : 'text-amber-100/70'}`}
                >
                  <Icon name="List" size={16} />
                </button>
              </div>
              <Button onClick={() => setShowForm(!showForm)} className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white">
                <Icon name="Plus" size={16} className="mr-1" />
                Add Brand
              </Button>
            </div>
          </div>

          {/* Add Brand Form */}
          {showForm && (
            <form className="border-t border-white/10 pt-4 space-y-3" onSubmit={handleAddBrand}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input
                  className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white placeholder:text-white/50"
                  placeholder="Brand name *"
                  value={brandForm.name}
                  onChange={(e) => setBrandForm({ ...brandForm, name: e.target.value })}
                  required
                />
                <select
                  className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white"
                  value={brandForm.category}
                  onChange={(e) => setBrandForm({ ...brandForm, category: e.target.value })}
                >
                  {categories.filter((c) => c !== 'all').map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
                <input
                  className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white placeholder:text-white/50"
                  placeholder="Website URL"
                  value={brandForm.website}
                  onChange={(e) => setBrandForm({ ...brandForm, website: e.target.value })}
                />
                <input
                  className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white placeholder:text-white/50"
                  placeholder="Email for outreach"
                  type="email"
                  value={brandForm.email}
                  onChange={(e) => setBrandForm({ ...brandForm, email: e.target.value })}
                />
                <select
                  className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white"
                  value={brandForm.status}
                  onChange={(e) => setBrandForm({ ...brandForm, status: e.target.value })}
                >
                  {statuses.filter((s) => s !== 'all').map((status) => (
                    <option key={status} value={status}>{status.charAt(0).toUpperCase() + status.slice(1)}</option>
                  ))}
                </select>
                <input
                  className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white placeholder:text-white/50"
                  placeholder="Tags (comma separated)"
                  value={brandForm.tags}
                  onChange={(e) => setBrandForm({ ...brandForm, tags: e.target.value })}
                />
                <textarea
                  className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm md:col-span-2 text-white placeholder:text-white/50"
                  placeholder="Notes, hook ideas, past performance..."
                  rows={2}
                  value={brandForm.notes}
                  onChange={(e) => setBrandForm({ ...brandForm, notes: e.target.value })}
                />
              </div>
              <div className="flex items-center gap-2">
                <Button type="submit" size="sm" className="bg-amber-500 hover:bg-amber-600 text-white">
                  {editingBrandId ? 'Update Brand' : 'Save Brand'}
                </Button>
                {editingBrandId && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setEditingBrandId(null);
                      setBrandForm({ name: '', category: 'CPG', website: '', email: '', notes: '', tags: '', status: 'prospect' });
                      setShowForm(false);
                    }}
                  >
                    Cancel
                  </Button>
                )}
              </div>
            </form>
          )}
        </div>

        {/* Brands Grid/List */}
        {allBrands.length === 0 ? (
          <div className="rounded-xl border border-white/10 bg-white/5 p-12 text-center shadow-sm">
            <Icon name="Search" size={48} className="text-amber-100/70 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">No brands found</h3>
            <p className="text-sm text-amber-100/80 mb-4">
              {brandSearch || brandFilter !== 'all' || statusFilter !== 'all'
                ? 'Try adjusting your filters or search terms'
                : 'Start building your brand database'}
            </p>
            <Button onClick={() => setShowForm(true)} className="bg-amber-500 hover:bg-amber-600 text-white">Add Your First Brand</Button>
          </div>
        ) : (
          <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4' : 'space-y-3'}>
            {allBrands.map((brand) => {
              const canEdit = !!profile?.id && brand.created_by === profile.id;
              const status = brand.status || 'prospect';
              
              return (
                <div key={brand.id} className={`rounded-xl border border-white/10 bg-white/5 p-5 shadow-sm hover:shadow-md transition-all ${viewMode === 'list' ? 'flex items-start gap-4' : ''}`}>
                  <div className={viewMode === 'list' ? 'flex-1' : ''}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Icon name={getCategoryIcon(brand.category)} size={20} className="text-blue-300" />
                        <h3 className="text-lg font-bold text-white">{brand.name}</h3>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full border ${getStatusColor(status)}`}>
                        {status}
                      </span>
                    </div>

                    <div className="space-y-2 mb-3">
                      <div className="flex items-center gap-2 text-xs text-amber-100/80">
                        <Icon name="Tag" size={12} />
                        <span>{brand.category}</span>
                      </div>
                      {brand.website && (
                        <a
                          href={brand.website.startsWith('http') ? brand.website : `https://${brand.website}`}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center gap-2 text-xs text-blue-300 hover:underline"
                        >
                          <Icon name="Globe" size={12} />
                          <span className="truncate">{brand.website}</span>
                        </a>
                      )}
                      {brand.email && (
                        <a
                          href={`mailto:${brand.email}`}
                          className="flex items-center gap-2 text-xs text-white hover:underline"
                        >
                          <Icon name="Mail" size={12} />
                          <span className="truncate">{brand.email}</span>
                        </a>
                      )}
                    </div>

                    {brand.notes && (
                      <p className="text-xs text-amber-100/80 mb-3 line-clamp-2">{brand.notes}</p>
                    )}

                    {brand.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-3">
                        {brand.tags.slice(0, 3).map((tag) => (
                          <span key={tag} className="text-[10px] px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-200 border border-purple-400/30">
                            #{tag}
                          </span>
                        ))}
                        {brand.tags.length > 3 && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/10 text-amber-100/80">
                            +{brand.tags.length - 3}
                          </span>
                        )}
                      </div>
                    )}

                    <div className="flex flex-wrap gap-2 pt-2 border-t border-white/10">
                      <Button size="xs" variant="outline" className="border-white/20 text-white hover:bg-white/10" onClick={() => handleCopyEmail(brand)}>
                        <Icon name="Copy" size={12} className="mr-1" />
                        Copy Email Template
                      </Button>
                      {canEdit && (
                        <>
                          <Button size="xs" variant="ghost" className="text-amber-100 hover:bg-white/10" onClick={() => {
                            setEditingBrandId(brand.id);
                            setBrandForm({
                              name: brand.name || '',
                              category: brand.category || 'CPG',
                              website: brand.website || '',
                              email: brand.email || '',
                              notes: brand.notes || '',
                              status: brand.status || 'prospect',
                              tags: Array.isArray(brand.tags) ? brand.tags.join(', ') : ''
                            });
                            setShowForm(true);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}>
                            <Icon name="Edit" size={12} className="mr-1" />
                            Edit
                          </Button>
                          <Button size="xs" variant="ghost" className="text-red-400 hover:bg-white/10" onClick={() => {
                            if (window.confirm(`Delete ${brand.name}?`)) {
                              ugcBrandsService.remove(brand.id).then(() => setBrands(brands.filter((b) => b.id !== brand.id)));
                            }
                          }}>
                            <Icon name="Trash" size={12} />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
      <NovaChat />
    </UGCLayout>
  );
};

const UGCBrandsPageWithProvider = () => (
  <NovaChatProvider>
    <UGCBrandsPage />
  </NovaChatProvider>
);

export default UGCBrandsPageWithProvider;
